<template>
    <div ref="container" :class="popoverClasses">
        <div
            v-if="$slots.base"
            ref="base"
            class="Popover__base"
        >
            <slot name="base" :toggle="toggle" :close="close" :is-open="isOpen" />
        </div>
        <!--
            PRODUCT-26110 Task 8b Step 2: portal-vue's <portal> -> native <Teleport>.
            `defer` (Vue 3.5+) covers Expandable's usage, where the receiving
            `#default-popover-target` div (tprm-portal's Main.vue) is a later sibling
            of the <router-view> this Popover typically renders inside.
        -->
        <Teleport v-if="teleport && isOpen" :to="`#${portalTarget}`" defer>
            <div
                :data-popover-id="popoverId"
                class="Popover__popper Popover__popper--teleported"
                :style="teleportedStyle"
                @click="clickedInside"
                @mousedown="dragging=true"
                @mouseup="dragging=false"
            >
                <slot :close="close" />
            </div>
        </Teleport>
        <div
            v-else-if="isOpen"
            ref="popper"
            class="Popover__popper"
            :style="nonTeleportedStyle"
            @click="clickedInside"
            @mousedown="dragging=true"
            @mouseup="dragging=false"
        >
            <slot :close="close" />
        </div>
    </div>
</template>
<style lang="scss">
    .Popover {
        position: relative;

        &__popper {
            position: absolute;
            // TODO[PRODUCT-25175] establish z-index variables and refactor to use
            z-index: 100000;

            &--teleported {
                position: fixed;
            }
        }
    }
</style>

<script lang="ts">
    import { Component, Prop, Ref, Vue, Watch } from '@evidentid/vue-property-decorator';

    @Component({
        emits: [ 'hide', 'toggle-close', 'toggle-open' ],
    })
    export default class Popover extends Vue {
        @Prop({ type: Boolean, default: false })
        private open!: boolean;

        /**
         * When true, the popover owns its open/close state internally.
         * Use the `toggle` and `close` slot props instead of :open / @hide.
         */
        @Prop({ type: Boolean, default: false })
        private selfContained!: boolean;

        @Prop({ type: String, default: 'start' })
        private positionX!: 'start' | 'end';

        @Prop({ type: String, default: 'bottom' })
        private positionY!: 'top' | 'bottom';

        /**
         * Horizontal offset in pixels (positive = right, negative = left)
         */
        @Prop({ type: Number, default: 0 })
        private offsetX!: number;

        /**
         * Vertical offset in pixels (positive = down, negative = up)
         */
        @Prop({ type: Number, default: 0 })
        private offsetY!: number;

        /**
         * Auto-flip behavior when popover doesn't fit in viewport.
         * 'x' = horizontal only, 'y' = vertical only, 'xy' or true = both axes.
         * null/false = no auto-flipping.
         */
        @Prop({ type: [ Boolean, String ], default: null })
        private autoFlip!: boolean | 'x' | 'y' | 'xy' | null;

        /** Estimated popover height used for flip threshold calculation (px). */
        @Prop({ type: Number, default: 300 })
        private flipEstimatedHeight!: number;

        /** Estimated popover width used for flip threshold calculation (px). */
        @Prop({ type: Number, default: 400 })
        private flipEstimatedWidth!: number;

        @Prop({ type: Boolean, default: false })
        private teleport!: boolean;

        /** CSS selector for elements that should not trigger outside-click close (e.g. teleported dropdowns). */
        @Prop({ type: String, default: '' })
        private outsideClickIgnoreSelector!: string;

        @Prop({ type: String, default: 'default-popover-target' })
        private portalTarget!: string;

        // Default off to save performance - only enable when teleported popover needs to follow scroll
        @Prop({ type: Boolean, default: false })
        private repositionOnScroll!: boolean;

        @Ref()
        private popper!: HTMLDivElement;

        @Ref()
        private base!: HTMLDivElement;

        private internalOpen = false;
        private dragging = false;
        // Resolved position after auto-flip; starts at prop defaults, recalculated on open.
        private computedPositionX: 'start' | 'end' = 'start';
        private computedPositionY: 'top' | 'bottom' = 'bottom';
        private computedOffsetX = 0;
        private computedOffsetY = 0;
        private unsubscribeBodyListener: Function | null = null;
        private unsubscribeScrollListener: Function | null = null;
        private teleportedStyle: Record<string, string> = {};
        private popoverId = `popover-${Math.random().toString(36).substring(2, 11)}`;
        private pendingScrollFrame: number | null = null;

        private get isOpen(): boolean {
            return this.selfContained ? this.internalOpen : this.open;
        }

        /**
         * Walk up from a DOM node to check if it lives inside this Popover's
         * teleported popper.  This avoids the querySelector race that occurs
         * when vue-portal tears down the old portal and mounts the new one –
         * querySelector can return the stale element while the click target
         * sits in the freshly-mounted one.
         */
        private isInsideTeleportedPopper(target: Node): boolean {
            let node: Node | null = target;
            while (node) {
                if (node instanceof HTMLElement && node.dataset.popoverId === this.popoverId) {
                    return true;
                }
                node = node.parentNode;
            }
            return false;
        }

        private get popoverClasses(): string {
            const basedClasses = `Popover Popover--x-${this.computedPositionX} Popover--y-${this.computedPositionY}`;
            const isOpenClasses = this.isOpen ? ' Popover--open' : '';
            return `${basedClasses}${isOpenClasses}`;
        }

        /**
         * Inline styles for non-teleported popover positioning
         */
        private get nonTeleportedStyle(): Record<string, string> {
            const style: Record<string, string> = {};
            const gap = 5; // Default gap between trigger and popover

            // Vertical positioning
            if (this.computedPositionY === 'bottom') {
                style.top = '100%';
                style.bottom = 'auto';
                style.marginTop = `${gap + this.computedOffsetY}px`;
            } else {
                style.bottom = '100%';
                style.top = 'auto';
                style.marginBottom = `${gap - this.computedOffsetY}px`;
            }

            // Horizontal positioning
            if (this.computedPositionX === 'start') {
                style.left = `${this.computedOffsetX}px`;
                style.right = 'auto';
            } else {
                style.right = `${-this.computedOffsetX}px`;
                style.left = 'auto';
            }

            return style;
        }

        @Watch('open')
        @Watch('internalOpen')
        @Watch('positionX')
        @Watch('positionY')
        @Watch('offsetX')
        @Watch('offsetY')
        private onPositionChange(): void {
            this.calculateFlipPosition();

            if (this.isOpen && this.teleport) {
                this.$nextTick(() => {
                    this.updateTeleportedPosition();
                    if (this.repositionOnScroll) {
                        this.addScrollListener();
                    }
                });
            } else {
                this.removeScrollListener();
            }
        }

        private calculateFlipPosition(): void {
            let newX = this.positionX as 'start' | 'end';
            let newY = this.positionY as 'top' | 'bottom';
            let newOffsetX = this.offsetX;
            let newOffsetY = this.offsetY;

            const flipX = this.autoFlip === 'x' || this.autoFlip === 'xy' || this.autoFlip === true;
            const flipY = this.autoFlip === 'y' || this.autoFlip === 'xy' || this.autoFlip === true;

            if (flipX || flipY) {
                const triggerEl = this.$el as HTMLElement | undefined;
                const triggerRect = triggerEl?.getBoundingClientRect();

                if (triggerRect) {
                    if (flipY && this.shouldFlipY(
                        window.innerHeight - triggerRect.bottom,
                        triggerRect.top,
                        this.flipEstimatedHeight,
                    )) {
                        newY = this.positionY === 'bottom' ? 'top' : 'bottom';
                        newOffsetY = -this.offsetY;
                    }

                    if (flipX && this.shouldFlipX(
                        window.innerWidth - triggerRect.left,
                        triggerRect.right,
                        this.flipEstimatedWidth,
                    )) {
                        newX = this.positionX === 'start' ? 'end' : 'start';
                        newOffsetX = -this.offsetX;
                    }
                }
            }

            this.computedPositionX = newX;
            this.computedPositionY = newY;
            this.computedOffsetX = newOffsetX;
            this.computedOffsetY = newOffsetY;
        }

        private shouldFlipY(spaceBelow: number, spaceAbove: number, height: number): boolean {
            if (this.positionY === 'bottom' && spaceBelow < height) {
                return spaceAbove >= height;
            }
            if (this.positionY === 'top' && spaceAbove < height) {
                return spaceBelow >= height;
            }
            return false;
        }

        private shouldFlipX(spaceRight: number, spaceLeft: number, width: number): boolean {
            if (this.positionX === 'start' && spaceRight < width) {
                return spaceLeft >= width;
            }
            if (this.positionX === 'end' && spaceLeft < width) {
                return spaceRight >= width;
            }
            return false;
        }

        private toggle(): void {
            if (this.selfContained) {
                this.internalOpen = !this.internalOpen;
                this.$emit(this.internalOpen ? 'toggle-open' : 'toggle-close');
            }
        }

        private close(): void {
            if (this.selfContained) {
                this.internalOpen = false;
            } else {
                this.$emit('hide');
            }
        }

        private clickedInside(event: Event): void {
            event.stopPropagation();
        }

        private clickedOutside(event: Event): void {
            if (!this.isOpen || this.dragging) {
                this.dragging = false;
                return;
            }

            const target = event.target as Node;

            // Check if click is inside the base element (trigger)
            if (this.base?.contains(target)) {
                this.dragging = false;
                return;
            }

            // Check if click is inside the popper.
            // For teleported poppers we walk UP from the target instead of using
            // querySelector → contains, because vue-portal can briefly have two
            // sets of portal content in the DOM during a close-then-reopen cycle,
            // causing querySelector to return the stale (outgoing) element.
            if (this.teleport ? this.isInsideTeleportedPopper(target) : this.popper?.contains(target)) {
                this.dragging = false;
                return;
            }

            // Check if click is inside an ignored element (e.g. a teleported dropdown like Quasar q-menu).
            if (this.outsideClickIgnoreSelector && (target as HTMLElement).closest?.(this.outsideClickIgnoreSelector)) {
                this.dragging = false;
                return;
            }

            // Click was outside both base and popper
            this.close();
            this.dragging = false;
        }

        private updateTeleportedPosition(): void {
            if (!this.base) {
                return;
            }

            const baseRect = this.base.getBoundingClientRect();
            const gap = 5;

            // Calculate vertical position using resolved (post-flip) values
            let top = 'auto';
            let bottom = 'auto';
            if (this.computedPositionY === 'bottom') {
                top = `${baseRect.bottom + gap + this.computedOffsetY}px`;
            } else {
                bottom = `${window.innerHeight - baseRect.top + gap - this.computedOffsetY}px`;
            }

            // Calculate horizontal position using resolved (post-flip) values
            let left = 'auto';
            let right = 'auto';
            if (this.computedPositionX === 'start') {
                left = `${baseRect.left + this.computedOffsetX}px`;
            } else {
                right = `${window.innerWidth - baseRect.right - this.computedOffsetX}px`;
            }

            this.teleportedStyle = { top, bottom, left, right };
        }

        private addListeners(): void {
            const onClickOutside = this.clickedOutside.bind(this);
            document.body.addEventListener('click', onClickOutside, true);
            this.unsubscribeBodyListener = () => {
                document.body.removeEventListener('click', onClickOutside, true);
            };
        }

        private removeListeners(): void {
            if (this.unsubscribeBodyListener) {
                this.unsubscribeBodyListener();
                this.unsubscribeBodyListener = null;
            }
        }

        private onScroll(): void {
            // Use requestAnimationFrame for performance - only update once per frame
            if (this.pendingScrollFrame) {
                return;
            }
            this.pendingScrollFrame = requestAnimationFrame(() => {
                this.pendingScrollFrame = null;
                if (this.isOpen && this.teleport) {
                    this.updateTeleportedPosition();
                }
            });
        }

        private addScrollListener(): void {
            if (this.unsubscribeScrollListener) {
                return;
            }
            const onScroll = this.onScroll.bind(this);
            // Use capture phase to catch scroll events from any container
            window.addEventListener('scroll', onScroll, true);
            this.unsubscribeScrollListener = () => {
                window.removeEventListener('scroll', onScroll, true);
            };
        }

        private removeScrollListener(): void {
            if (this.pendingScrollFrame) {
                cancelAnimationFrame(this.pendingScrollFrame);
                this.pendingScrollFrame = null;
            }
            if (this.unsubscribeScrollListener) {
                this.unsubscribeScrollListener();
                this.unsubscribeScrollListener = null;
            }
        }

        private created() {
            // Sync computed position with props before first render
            this.calculateFlipPosition();
        }

        private mounted() {
            this.addListeners();
        }

        private unmounted() {
            this.removeListeners();
            this.removeScrollListener();
        }
    }
</script>
